import React, { CSSProperties, useCallback, useMemo } from 'react';
import Modal, { Props as ModalProps } from '@hero/ui-kit/components/Modal';
import Button from '@hero/ui-kit/inputs/Button';
import useComponentPadding from '@hero/ui-kit/hooks/useComponentPadding';
import useSectionPadding from '@hero/ui-kit/hooks/useSectionPadding';

import ModalFlowHeader from '../../../../components/ModalFlowHeader';
import { TransferToD2CProvider, useTransferToD2C } from './context/TransferToD2CContext';
import MitigationOffers from './steps/MitigationOffers';
import PlanSelection from './steps/PlanSelection';
import HardshipSelection from './steps/HardshipSelection';
import CollectPayment from './steps/CollectPayment';
import SuccessReport from './steps/SuccessReport';
import { EnterpriseEnrollmentExtended } from '../../api/useGetEnterpriseMemberDetails';
import getMemberFullName from '../../utils/getMemberFullName';

interface TransferToD2CProps {
    heroUserId: number;
    enrollment: EnterpriseEnrollmentExtended;
    disabled?: boolean;
}

const STEPS = ['Select Plan', 'Plan Details', 'Payment'];

const TransferToD2CModal: React.FC<{
    enrollment: EnterpriseEnrollmentExtended;
    onClose: () => void;
}> = ({ enrollment, onClose }) => {
    const { step, state, heading } = useTransferToD2C();

    const eyebrow = `${getMemberFullName(enrollment)} / ${enrollment.member?.contact?.email || enrollment.contact?.email || ''} / ID: ${enrollment.member?.id || ''}`;

    const modalType: ModalProps['type'] = useMemo(() => {
        if (state === 'success') return 'success';
        return 'warn';
    }, [state]);

    const modalPadding = useSectionPadding({ padding: 'regular' });
    const innerWrapPadding = useComponentPadding({ padding: 'regular' });

    const modalStyles: CSSProperties = {
        width: '100rem',
        ...modalPadding
    };

    const innerWrapStyles: CSSProperties = {
        alignSelf: 'stretch',
        ...innerWrapPadding
    };

    return (
        <Modal
            width="page"
            type={modalType}
            styles={modalStyles}
            noDefaultPadding
            isCancelable
            onClose={onClose}
        >
            <ModalFlowHeader
                heading={heading}
                eyebrow={state !== 'success' ? eyebrow : undefined}
                steps={state !== 'success' ? STEPS : undefined}
                currentStep={step}
            />
            <div style={innerWrapStyles}>
                {state === 'mitigation-offers' && <MitigationOffers />}
                {state === 'plan-offers' && <PlanSelection />}
                {state === 'hardship-offers' && <HardshipSelection />}
                {state === 'collect-payment-method' && <CollectPayment />}
                {state === 'success' && <SuccessReport onClose={onClose} />}
            </div>
        </Modal>
    );
};

const formatTransferDate = (ts: string) => {
    const date = new Date(ts);
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yyyy = date.getFullYear();
    const hh = String(date.getHours()).padStart(2, '0');
    const min = String(date.getMinutes()).padStart(2, '0');
    return `${mm}/${dd}/${yyyy} at ${hh}:${min}`;
};

const TransferToD2C: React.FC<TransferToD2CProps> = ({ heroUserId, enrollment, disabled }) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [showTooltip, setShowTooltip] = React.useState(false);
    const tooltipWrapperRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (!showTooltip) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (
                tooltipWrapperRef.current &&
                !tooltipWrapperRef.current.contains(e.target as Node)
            ) {
                setShowTooltip(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showTooltip]);

    const handleClose = useCallback(() => {
        setIsOpen(false);
    }, []);

    const suggestedResponse =
        "Since your device has been returned, we're unable to transfer your membership to a D2C subscription. To continue with Hero, you can purchase a new device at herohealth.com.";

    const [copied, setCopied] = React.useState(false);

    const handleCopySuggestedResponse = useCallback(() => {
        navigator.clipboard.writeText(suggestedResponse).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    }, []);

    const lastLog = enrollment.logs?.membership_logs?.length
        ? [...enrollment.logs.membership_logs].sort(
              (a, b) => new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime()
          )[0]
        : undefined;
    const transferCompleted = lastLog?.event === 'transfer';
    const transferTimestamp = lastLog?.timestamp;

    if (transferCompleted) {
        return (
            <div>
                <Button size="small" width="large" disabled noDefaultMargin>
                    Transfer to D2C
                </Button>
                <p style={{ margin: '0.4rem 0 0 0', fontSize: '1.1rem', color: '#888' }}>
                    Transfer to D2C completed on {formatTransferDate(transferTimestamp!)}
                </p>
            </div>
        );
    }

    if (disabled) {
        return (
            <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                    <Button size="small" width="large" disabled noDefaultMargin>
                        Transfer to D2C
                    </Button>
                    <div
                        ref={tooltipWrapperRef}
                        style={{ position: 'relative', display: 'inline-block' }}
                    >
                        <span
                            onClick={() => setShowTooltip((prev) => !prev)}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '2rem',
                                height: '2rem',
                                borderRadius: '50%',
                                border: '1px solid darkorange',
                                fontSize: '1.2rem',
                                color: 'darkorange',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                userSelect: 'none'
                            }}
                        >
                            ?
                        </span>
                        {showTooltip && (
                            <div
                                style={{
                                    position: 'absolute',
                                    bottom: '100%',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    marginBottom: '0.8rem',
                                    padding: '1.2rem',
                                    backgroundColor: '#333',
                                    color: '#fff',
                                    borderRadius: '0.4rem',
                                    fontSize: '1.2rem',
                                    lineHeight: '1.6',
                                    width: '36rem',
                                    zIndex: 1000,
                                    whiteSpace: 'normal',
                                    userSelect: 'text'
                                }}
                            >
                                <p style={{ margin: '0 0 0.8rem 0', fontWeight: 'bold' }}>
                                    Agent instruction:
                                </p>
                                <p style={{ margin: '0 0 0.8rem 0' }}>
                                    If the member is requesting a D2C subscription, direct them to
                                    purchase a new device at herohealth.com.
                                </p>
                                <p style={{ margin: '0 0 0.8rem 0', fontWeight: 'bold' }}>
                                    Suggested response:
                                </p>
                                <p style={{ margin: '0 0 0.8rem 0' }}>{suggestedResponse}</p>
                                <button
                                    onClick={handleCopySuggestedResponse}
                                    style={{
                                        background: 'transparent',
                                        border: '1px solid #aaa',
                                        color: '#fff',
                                        borderRadius: '0.3rem',
                                        padding: '0.4rem 0.8rem',
                                        fontSize: '1.1rem',
                                        cursor: 'pointer'
                                    }}
                                >
                                    {copied ? 'Copied!' : 'Copy suggested response'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
                <p
                    style={{
                        margin: '0.4rem 0 0 0',
                        fontSize: '1.1rem',
                        color: '#888'
                    }}
                >
                    Transfer is unavailable - device returned.
                </p>
            </div>
        );
    }

    return (
        <>
            <Button size="small" onClick={() => setIsOpen(true)} noDefaultMargin>
                Transfer to D2C
            </Button>
            {isOpen && (
                <TransferToD2CProvider heroUserId={heroUserId}>
                    <TransferToD2CModal enrollment={enrollment} onClose={handleClose} />
                </TransferToD2CProvider>
            )}
        </>
    );
};

export default TransferToD2C;
